import dayjs from 'dayjs';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { Field } from '/@/types/field';
import { isCornMaturity } from './corn-stage';
import type { StageMessage } from './shared';

/**
 * Post-maturity harvest-date banner — corn only, advisor-facing.
 *
 * Shown to advisor-type users when an active corn crop monitoring has been at maturity
 * (`phenology === 'maturity'`) for more than 30 days and harvest has not been registered yet.
 * It surfaces the expected harvest date (14.5% grain moisture) and nudges registering harvest
 * to unlock the campaign report.
 *
 * Returns `null` for non-corn/non-advisor users, before the 30-day window, or once harvest is
 * registered.
 */
export const usePostMaturityHarvestStageMessage = ({
    cropMonitoring,
}: {
    cropMonitoring?: CropMonitoring;
    field: Field;
}): StageMessage | null => {
    const isValidatedBayerUserType = useIsValidatedBayerUserType();
    const corn = cropMonitoring?.cornCropMonitoring;

    if (!isValidatedBayerUserType || !isCornMaturity(cropMonitoring, { withoutHarvest: true })) return null;
    if (!corn?.expectedMaturityDate) return null;

    const expectedMaturityDate = dayjs(corn.expectedMaturityDate);
    if (!expectedMaturityDate.isValid()) return null;

    const maturityDatePlus30Days = expectedMaturityDate.add(30, 'day');
    if (!dayjs().isAfter(maturityDatePlus30Days)) return null;

    const expectedHarvestDate = corn.expectedHarvestDate ? dayjs(corn.expectedHarvestDate).format('DD/MM/YYYY') : null;
    if (!expectedHarvestDate)
        return {
            title: _('El productor está próximo a cosechar'),
            message: _('Cuando el productor coseche, registrá la cosecha para acceder al reporte de campaña.'),
            actions: [{ label: _('Obtener reporte'), variant: 'contained', action: 'harvest-report' }],
        };

    return {
        title: _('La fecha probable de cosecha es el {{date}}.', { date: expectedHarvestDate }),
        message: _(
            'Calculamos la fecha a partir del momento en que el cultivo alcanza el 14,5% de humedad estable. Cuando el productor coseche, registrá la cosecha para acceder al reporte de campaña.'
        ),
        actions: [{ label: _('Obtener reporte'), variant: 'contained', action: 'harvest-report' }],
    };
};
